import { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import TermsModal from './TermsModal';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

const SUPABASE_FUNCTIONS_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL?.replace(
  '.supabase.co',
  '.supabase.co/functions/v1'
);

export default function SignupSection() {
  const { ref, isVisible } = useScrollAnimation();
  const [formData, setFormData] = useState({ id: '', name: '', phone: '', password: '', passwordConfirm: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [modal, setModal] = useState<'terms' | 'privacy' | null>(null);
  const [smsStatus, setSmsStatus] = useState<'none' | 'sent' | 'failed'>('none');
  const [showPw, setShowPw] = useState(false);
  const [showPwConfirm, setShowPwConfirm] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const scrollToProducts = () => {
    setTimeout(() => {
      const el = document.getElementById('products');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 1200);
  };

  // SMS 알림 발송
  const sendSignupSms = async (name: string, phone: string, memberId: string) => {
    try {
      const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/sms-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: phone,
          type: 'signup',
          name,
          memberId,
          phone,
          orderNumber: `SIGNUP-${Date.now()}`,
          totalPrice: '0',
          paymentMethod: '회원가입',
          items: `신규 회원가입 완료 - ID: ${memberId}`,
        }),
      });
      if (res.ok) {
        setSmsStatus('sent');
      } else {
        setSmsStatus('failed');
      }
    } catch {
      setSmsStatus('failed');
    }
  };

  // ERP 포인트 조회 연동 (globalaitrade.kr)
  const linkErpMember = async (memberId: string, phone: string) => {
    try {
      const formBody = new URLSearchParams({
        action_mode: 'point_check',
        user_id: memberId,
        return_page: 'https://readdy.ai/project/fab39877-057d-4165-9475-a35374cb4014/erp-callback',
      });
      await fetch('http://globalaitrade.kr/kwebmall/erp_mobile/form_value_send2.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
        mode: 'no-cors',
      });
    } catch {
      // ERP 연동 실패해도 가입은 진행
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg('');

    if (!formData.id.trim()) { setErrorMsg('아이디를 입력해 주세요.'); return; }
    if (!formData.name.trim()) { setErrorMsg('이름을 입력해 주세요.'); return; }
    if (!formData.phone.trim()) { setErrorMsg('전화번호를 입력해 주세요.'); return; }

    const phoneRegex = /^[0-9]{10,11}$/;
    if (!phoneRegex.test(formData.phone.replace(/-/g, ''))) {
      setErrorMsg('올바른 전화번호를 입력해 주세요. (예: 01012345678)');
      return;
    }

    if (!formData.password.trim()) { setErrorMsg('비밀번호를 입력해 주세요.'); return; }
    if (formData.password.length < 6) { setErrorMsg('비밀번호는 6자 이상 입력해 주세요.'); return; }
    if (formData.password !== formData.passwordConfirm) {
      setErrorMsg('비밀번호가 일치하지 않습니다.');
      return;
    }

    setStatus('loading');

    try {
      const cleanId = formData.id.trim().toUpperCase();
      const cleanPhone = formData.phone.replace(/-/g, '').trim();

      // 1. 기존 회원 여부 확인 (탈퇴 회원 포함)
      const { data: existingMember } = await supabase
        .from('members')
        .select('member_id, is_withdrawn')
        .eq('member_id', cleanId)
        .maybeSingle();

      if (existingMember) {
        if (!existingMember.is_withdrawn) {
          // 활성 회원이면 중복 아이디 오류
          setStatus('error');
          setErrorMsg('이미 사용 중인 아이디입니다. 다른 아이디를 입력해 주세요.');
          return;
        }

        // 탈퇴 회원이면 재가입 처리 (기존 레코드 업데이트)
        const { error: updateError } = await supabase
          .from('members')
          .update({
            member_name: formData.name.trim(),
            phone: cleanPhone,
            password_hash: formData.password,
            points_balance: 0,
            discount_used: false,
            discount_code: null,
            is_withdrawn: false,
            withdrawn_at: null,
          })
          .eq('member_id', cleanId);

        if (updateError) {
          setStatus('error');
          setErrorMsg('가입 중 오류가 발생했습니다. 다시 시도해 주세요.');
          return;
        }
      } else {
        // 신규 회원 등록
        const { error: insertError } = await supabase
          .from('members')
          .insert({
            member_id: cleanId,
            member_name: formData.name.trim(),
            phone: cleanPhone,
            password_hash: formData.password,
            points_balance: 0,
            discount_used: false,
          });

        if (insertError) {
          setStatus('error');
          setErrorMsg('가입 중 오류가 발생했습니다. 다시 시도해 주세요.');
          return;
        }
      }

      // 2. 폼 제출 (기존 readdy form)
      const body = new URLSearchParams({
        id: cleanId,
        name: formData.name.trim(),
        phone: cleanPhone,
      });
      await fetch('https://readdy.ai/api/form/d79hftprcamcd36dbhi0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      // 3. SMS 알림 발송 (비동기)
      sendSignupSms(formData.name.trim(), cleanPhone, cleanId);

      // 4. ERP 연동 (비동기)
      linkErpMember(cleanId, cleanPhone);

      setStatus('success');
      setFormData({ id: '', name: '', phone: '', password: '', passwordConfirm: '' });
      scrollToProducts();
    } catch {
      setStatus('error');
      setErrorMsg('네트워크 오류가 발생했습니다. 다시 시도해 주세요.');
    }
  };

  return (
    <>
      {modal && <TermsModal type={modal} onClose={() => setModal(null)} />}

      <section id="signup" className="py-16 md:py-24 px-5 md:px-8 bg-emerald-50">
        <div
          ref={ref}
          className={`max-w-2xl mx-auto transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* 헤더 */}
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-4 tracking-wide">
              MEMBERSHIP
            </span>
            <h2
              className="text-2xl md:text-3xl font-bold text-gray-900 mb-3"
              style={{ fontFamily: 'Noto Serif KR, serif' }}
            >
              회원가입
            </h2>
            <p className="text-sm md:text-base text-gray-500">
              회원가입 후 <strong className="text-emerald-600">첫 구매 20% 할인</strong> 혜택을 받아보세요
            </p>
          </div>

          {/* 폼 카드 */}
          <div className="bg-white rounded-2xl p-8 md:p-10">
            {status === 'success' ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-emerald-600 w-8 h-8 flex items-center justify-center"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">가입이 완료되었습니다!</h3>
                <p className="text-sm text-gray-500 mb-1">자연담은 회원이 되신 것을 환영합니다.</p>
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium mb-4 ${
                  smsStatus === 'sent' ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-500'
                }`}>
                  <i className="ri-message-2-line w-3 h-3 flex items-center justify-center"></i>
                  {smsStatus === 'sent' ? 'SMS 가입 완료 알림이 발송되었습니다' : 'SMS 알림 처리 중...'}
                </div>
                <p className="text-xs text-emerald-600 mb-6 flex items-center justify-center gap-1">
                  <i className="ri-arrow-down-line w-3 h-3 flex items-center justify-center animate-bounce"></i>
                  잠시 후 제품 섹션으로 이동합니다
                </p>
                <button
                  onClick={() => {
                    setStatus('idle');
                    setSmsStatus('none');
                    document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="px-6 py-2.5 bg-emerald-600 text-white rounded-full text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  지금 제품 보러가기
                </button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                {/* 아이디 */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="signup-id" className="text-sm font-semibold text-gray-700">
                    아이디 <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-user-line text-base"></i>
                    </span>
                    <input
                      id="signup-id"
                      type="text"
                      name="id"
                      value={formData.id}
                      onChange={handleChange}
                      placeholder="사용할 아이디를 입력하세요"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition-all"
                    />
                  </div>
                </div>

                {/* 이름 */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="signup-name" className="text-sm font-semibold text-gray-700">
                    이름 <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-id-card-line text-base"></i>
                    </span>
                    <input
                      id="signup-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="실명을 입력하세요"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition-all"
                    />
                  </div>
                </div>

                {/* 전화번호 */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="signup-phone" className="text-sm font-semibold text-gray-700">
                    전화번호 <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-phone-line text-base"></i>
                    </span>
                    <input
                      id="signup-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="01012345678"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition-all"
                    />
                  </div>
                </div>

                {/* 비밀번호 */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="signup-password" className="text-sm font-semibold text-gray-700">
                    비밀번호 <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-lock-line text-base"></i>
                    </span>
                    <input
                      id="signup-password"
                      type={showPw ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="비밀번호 6자 이상 입력"
                      className="w-full pl-10 pr-11 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(!showPw)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={`${showPw ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">영문, 숫자 조합 6자 이상</p>
                </div>

                {/* 비밀번호 확인 */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="signup-password-confirm" className="text-sm font-semibold text-gray-700">
                    비밀번호 확인 <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                      <i className="ri-lock-2-line text-base"></i>
                    </span>
                    <input
                      id="signup-password-confirm"
                      type={showPwConfirm ? 'text' : 'password'}
                      name="passwordConfirm"
                      value={formData.passwordConfirm}
                      onChange={handleChange}
                      placeholder="비밀번호를 다시 입력하세요"
                      className={`w-full pl-10 pr-11 py-3 border rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                        formData.passwordConfirm && formData.password !== formData.passwordConfirm
                          ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                          : formData.passwordConfirm && formData.password === formData.passwordConfirm
                          ? 'border-emerald-400 focus:border-emerald-400 focus:ring-emerald-100'
                          : 'border-gray-200 focus:border-emerald-400 focus:ring-emerald-100'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwConfirm(!showPwConfirm)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={`${showPwConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                    </button>
                    {formData.passwordConfirm && (
                      <span className={`absolute right-10 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center ${
                        formData.password === formData.passwordConfirm ? 'text-emerald-500' : 'text-red-400'
                      }`}>
                        <i className={`${formData.password === formData.passwordConfirm ? 'ri-check-line' : 'ri-close-line'} text-sm`}></i>
                      </span>
                    )}
                  </div>
                  {formData.passwordConfirm && formData.password !== formData.passwordConfirm && (
                    <p className="text-xs text-red-500">비밀번호가 일치하지 않습니다</p>
                  )}
                  {formData.passwordConfirm && formData.password === formData.passwordConfirm && (
                    <p className="text-xs text-emerald-600">비밀번호가 일치합니다</p>
                  )}
                </div>

                {/* 에러 메시지 */}
                {errorMsg && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                    <span className="w-4 h-4 flex items-center justify-center text-red-500 flex-shrink-0">
                      <i className="ri-error-warning-line text-sm"></i>
                    </span>
                    <p className="text-xs text-red-600">{errorMsg}</p>
                  </div>
                )}

                {/* 제출 버튼 */}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full py-3.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-1"
                >
                  {status === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                      처리 중...
                    </>
                  ) : (
                    <>
                      <i className="ri-user-add-line w-4 h-4 flex items-center justify-center"></i>
                      회원가입
                    </>
                  )}
                </button>

                {/* 약관 동의 문구 */}
                <p className="text-xs text-gray-400 text-center leading-relaxed">
                  가입 시 자연담은의{' '}
                  <button
                    type="button"
                    onClick={() => setModal('terms')}
                    className="text-emerald-600 font-semibold underline underline-offset-2 hover:text-emerald-700 cursor-pointer"
                  >
                    이용약관
                  </button>
                  {' '}및{' '}
                  <button
                    type="button"
                    onClick={() => setModal('privacy')}
                    className="text-emerald-600 font-semibold underline underline-offset-2 hover:text-emerald-700 cursor-pointer"
                  >
                    개인정보처리방침
                  </button>
                  에 동의하는 것으로 간주됩니다.
                </p>
              </form>
            )}
          </div>

          {/* 혜택 안내 */}
          <div className="grid grid-cols-3 gap-3 mt-6">
            {[
              { icon: 'ri-gift-line', label: '첫 구매 20% 할인' },
              { icon: 'ri-truck-line', label: '3만원 이상 무료배송' },
              { icon: 'ri-coin-line', label: '포인트 적립 혜택' },
            ].map((item) => (
              <div
                key={item.label}
                className="flex flex-col items-center gap-2 py-4 px-3 bg-white rounded-xl text-center"
              >
                <div className="w-9 h-9 flex items-center justify-center bg-emerald-100 rounded-full">
                  <i className={`${item.icon} text-emerald-600 text-base w-5 h-5 flex items-center justify-center`}></i>
                </div>
                <span className="text-xs text-gray-600 font-medium leading-tight">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
